import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  TextField
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import EmailIcon from '@mui/icons-material/Email';
import { api } from '../api/client';

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [remainingAttempts, setRemainingAttempts] = useState(0);

  // Auto-verify if token is provided in URL (backward compatibility)
  useEffect(() => {
    if (token) {
      setLoading(true);
      verifyEmailWithToken();
    }
  }, [token]);
  
  // Countdown timer for resend
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [resendCooldown]);

  const verifyEmailWithToken = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Call the backend verification endpoint with token
      const response = await api.get(`/api/auth/email-verification/verify?token=${token}`);
      
      if (response.status === 200 && response.data) {
        setSuccess(true);
        setEmail(response.data.email || null);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Email verified successfully! You can now log in.',
              verified: true 
            } 
          });
        }, 3000);
      } else {
        setError(response.data?.message || 'Verification failed. Please try again.');
      }
    } catch (err: any) {
      const errorData = err.response?.data;
      const errorMessage = errorData?.message || 
                          errorData?.error || 
                          'Failed to verify email. The link may be invalid or expired.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const verifyEmailWithCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter the 6-digit verification code');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      // Call the backend verification endpoint with code
      const response = await api.post('/api/auth/email-verification/verify', {
        code: verificationCode,
        email: emailInput || undefined
      });
      
      if (response.status === 200 && response.data) {
        setSuccess(true);
        setEmail(response.data.email || emailInput || null);
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { 
              message: 'Email verified successfully! You can now log in.',
              verified: true 
            } 
          });
        }, 3000);
      } else {
        setError(response.data?.message || 'Verification failed. Please try again.');
      }
    } catch (err: any) {
      const errorData = err.response?.data;
      const errorMessage = errorData?.message || 
                          errorData?.error || 
                          'Failed to verify email. Please check the code and try again.';
      setError(errorMessage);
      
      // Show remaining attempts if available
      if (errorData?.remainingAttempts !== undefined) {
        setRemainingAttempts(errorData.remainingAttempts);
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleResendCode = async () => {
    if (!emailInput || !emailInput.trim()) {
      setError('Please enter your email address to resend the verification code');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.post('/api/auth/email-verification/send', {
        usernameOrEmail: emailInput.trim()
      });
      
      if (response.status === 200) {
        setSuccess('Verification code sent successfully! Please check your email.');
        setCanResend(false);
        setResendCooldown(60); // 60 seconds cooldown
      } else {
        setError('Failed to resend verification code. Please try again.');
      }
    } catch (err: any) {
      const errorData = err.response?.data;
      setError(errorData?.message || 'Failed to resend verification code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToLogin = () => {
    navigate('/login', { 
      state: { 
        message: 'Email verified successfully! You can now log in.',
        verified: true 
      } 
    });
  };

  // If token is provided, show loading/result states
  if (token) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          {loading && (
            <Box>
              <CircularProgress size={60} sx={{ mb: 3 }} />
              <Typography variant="h5" gutterBottom>
                Verifying your email...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait while we verify your email address.
              </Typography>
            </Box>
          )}

          {success && !loading && (
            <Box>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="success.main">
                Email Verified Successfully!
              </Typography>
              {email && (
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Your email address <strong>{email}</strong> has been verified.
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                You can now log in to your account. Redirecting to login page...
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGoToLogin}
                sx={{ mt: 2 }}
              >
                Go to Login
              </Button>
            </Box>
          )}

          {error && !loading && (
            <Box>
              <ErrorIcon sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom color="error.main">
                Verification Failed
              </Typography>
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {error}
              </Alert>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Possible reasons:
              </Typography>
              <Box sx={{ textAlign: 'left', mb: 3 }}>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>The verification link has expired (links expire after 24 hours)</li>
                  <li>The verification link has already been used</li>
                  <li>The verification link is invalid</li>
                </Typography>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/login')}
                sx={{ mr: 2 }}
              >
                Go to Login
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/register')}
              >
                Register Again
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    );
  }

  // If no token, show code entry form
  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        <EmailIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Verify Your Email Address
        </Typography>
        
        {success && !loading && (
          <Box>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" gutterBottom color="success.main">
              Email Verified Successfully!
            </Typography>
            {email && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Your email address <strong>{email}</strong> has been verified.
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You can now log in to your account. Redirecting to login page...
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGoToLogin}
              sx={{ mt: 2 }}
            >
              Go to Login
            </Button>
          </Box>
        )}

        {!success && (
          <>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Enter the 6-digit verification code sent to your email address.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                {error}
                {remainingAttempts > 0 && (
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    {remainingAttempts} attempt(s) remaining
                  </Typography>
                )}
              </Alert>
            )}

            <Box sx={{ mb: 3 }}>
              <TextField
                label="Email Address"
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                fullWidth
                placeholder="your.email@example.com"
                sx={{ mb: 2 }}
                helperText="Enter the email address you used to register"
              />
              
              <TextField
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setVerificationCode(value);
                  setError(null);
                }}
                fullWidth
                required
                placeholder="000000"
                inputProps={{ 
                  maxLength: 6, 
                  pattern: "[0-9]*",
                  style: { 
                    textAlign: 'center', 
                    fontSize: '1.5rem',
                    letterSpacing: '0.5rem',
                    fontFamily: 'monospace'
                  }
                }}
                error={!!error}
                helperText="Enter the 6-digit code from your email"
              />
            </Box>

            <Button
              variant="contained"
              color="primary"
              onClick={verifyEmailWithCode}
              disabled={loading || verificationCode.length !== 6}
              fullWidth
              size="large"
              sx={{ mb: 2 }}
            >
              {loading ? 'Verifying...' : 'Verify Email'}
            </Button>

            <Box sx={{ mt: 3 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Didn't receive the code?
              </Typography>
              
              {canResend ? (
                <Button
                  onClick={handleResendCode}
                  variant="outlined"
                  size="medium"
                  disabled={loading || !emailInput || !emailInput.trim()}
                  sx={{
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                >
                  {loading ? 'Sending...' : 'Resend Code'}
                </Button>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Resend available in:
                  </Typography>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      backgroundColor: 'primary.50',
                      border: '1px solid',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  >
                    {resendCooldown}s
                  </Box>
                </Box>
              )}
            </Box>

            <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant="text"
                onClick={() => navigate('/login')}
                sx={{ mr: 2 }}
              >
                Back to Login
              </Button>
              <Button
                variant="text"
                onClick={() => navigate('/register')}
              >
                Register Again
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default VerifyEmail;

